/**
 * S4 — MATRIZ DE PERMISOS (verdad única).
 *
 * Esta es la ÚNICA definición de qué puede hacer cada rol. De aquí derivan:
 *   · los guards de la API (apps/api consume `permissionFor(method, path)`)
 *   · la navegación y el gating de la UI (apps/web consume `can()`)
 *   · las políticas RLS de Postgres (packages/db las genera desde `rlsPredicate()`)
 *   · los tests e2e por rol (que fallan si un bettor ve algo prohibido)
 *
 * Regla §6: si un permiso existe en dos lugares, es un bug. El test de paridad
 * `test/parity.test.ts` rompe el build si la matriz y las rutas se desincronizan.
 */
import { z } from 'zod';

export const RoleSchema = z.enum(['admin', 'operator', 'cashier', 'bettor', 'viewer']);
export type Role = z.infer<typeof RoleSchema>;
export const ROLES = RoleSchema.options;

/** Orden de privilegio (mayor índice = más privilegio). Útil para comparaciones. */
export const ROLE_RANK: Record<Role, number> = {
  viewer: 0,
  bettor: 1,
  cashier: 2,
  operator: 3,
  admin: 4,
};

export const ResourceSchema = z.enum([
  'fight',
  'bet',
  'payout',
  'history',
  'settings',
  'preset',
  'user',
  'audit',
  'export',
  'terminal',
  'metric',
  'security',
]);
export type Resource = z.infer<typeof ResourceSchema>;

export const ActionSchema = z.enum([
  'read',
  'read_own',
  'read_all',
  'create',
  'update',
  'lock',
  'settle',
  'cancel',
  'pay',
  'approve',
  'manage',
]);
export type Action = z.infer<typeof ActionSchema>;

export type Permission = `${Resource}:${Action}`;

/**
 * Matriz rol → permisos. Ver §9.2 del plan.
 * `bettor` (usuario sin permisos) ve EXACTAMENTE: pelea actual + sus propias apuestas.
 * `viewer` es el rol del HUD/OBS: solo lectura de la pelea viva.
 */
export const PERMISSION_MATRIX: Record<Role, readonly Permission[]> = {
  viewer: ['fight:read'],

  bettor: ['fight:read', 'bet:read_own'],

  cashier: [
    'fight:read',
    'bet:read_all',
    'payout:read',
    'payout:pay',
    'history:read',
    'metric:read',
    'terminal:read',
  ],

  operator: [
    'fight:read',
    'fight:lock',
    'bet:read_all',
    'bet:create',
    'payout:read',
    'history:read',
    'metric:read',
    'export:read',
    'terminal:read',
  ],

  admin: [
    'fight:read',
    'fight:create',
    'fight:update',
    'fight:lock',
    'fight:settle',
    'fight:cancel',
    'bet:read_all',
    'bet:read_own',
    'bet:create',
    'payout:read',
    'payout:pay',
    'history:read',
    'metric:read',
    'settings:read',
    'settings:update',
    'preset:read',
    'preset:create',
    'security:read',
    'security:manage',
    'preset:update',
    'preset:approve',
    'user:read',
    'user:update',
    'audit:read',
    'export:read',
    'terminal:read',
    'terminal:update',
  ],
};

/** ¿El rol tiene el permiso? Único punto de verdad para autorización de UI y API. */
export function can(role: Role, resource: Resource, action: Action): boolean {
  return PERMISSION_MATRIX[role].includes(`${resource}:${action}` as Permission);
}

/** Lista completa de permisos efectivos (lo que devuelve `GET /me`). */
export function permissionsFor(role: Role): readonly Permission[] {
  return PERMISSION_MATRIX[role];
}

/**
 * Alcance de lectura de datos por rol. La API y la RLS lo usan para filtrar filas.
 *  · all     → todas las filas de la organización
 *  · own     → solo las filas cuyo user_id == actor
 *  · pending → solo filas en estado pagable (caja operativa)
 *  · none    → sin acceso
 */
export type DataScope = 'all' | 'own' | 'pending' | 'none';

export function scopeFor(role: Role, resource: Resource): DataScope {
  switch (resource) {
    case 'bet':
      if (can(role, 'bet', 'read_all')) return 'all';
      if (can(role, 'bet', 'read_own')) return 'own';
      return 'none';
    case 'payout':
      if (role === 'cashier') return 'pending';
      if (can(role, 'payout', 'read')) return 'all';
      return 'none';
    case 'fight':
      return can(role, 'fight', 'read') ? 'all' : 'none';
    case 'history':
      return can(role, 'history', 'read') ? 'all' : 'none';
    default:
      return can(role, resource as Resource, 'read') ? 'all' : 'none';
  }
}

/**
 * Predicado SQL de RLS por rol. packages/db lo usa para generar las políticas,
 * garantizando que la capa 2 (BD) sea coherente con la matriz. `orgParam` es el
 * nombre del parámetro de sesión con el org_id del token.
 */
export function rlsPredicate(role: Role, resource: Resource): string | null {
  const org = `current_setting('app.org_id', true)`;
  const uid = `current_setting('app.user_id', true)`;
  const scope = scopeFor(role, resource);
  if (scope === 'none') return null;
  const base = `org_id = ${org}`;
  switch (resource) {
    case 'bet':
      if (scope === 'all') return base;
      if (scope === 'own') {
        // Un bettor ve sus apuestas (por user_id) y, además, los boletos de ventanilla
        // emitidos a su nombre de ciudadano vinculado.
        return `(${base} AND (user_id = ${uid} OR citizen_name = current_setting('app.citizen_name', true)))`;
      }
      return null;
    case 'fight':
      return base;
    case 'payout':
      return base;
    case 'audit':
      return role === 'admin' ? base : null;
    case 'user':
      return role === 'admin' ? base : null;
    case 'preset':
      return role === 'admin' ? base : null;
    case 'terminal':
      return base;
    default:
      return base;
  }
}

/** Roles que pueden operar la ventanilla / caja / admin (para rutas protegidas). */
export const STAFF_ROLES: readonly Role[] = ['admin', 'operator', 'cashier'];

/** Rol por defecto para un usuario recién creado sin permisos (apostador). */
export const DEFAULT_ROLE: Role = 'bettor';
